import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api.js'
import { ErrorBox, Skeleton, useAsync } from '../../components/Ui.jsx'
import { useRequestSeed } from '../../components/PublicLayout.jsx'
import { diffDays, fmtDate, lakh, todayISO } from '../../lib/format.js'
import { monogram } from '../../lib/monogram.js'

const SORTS = [
  { value: 'soon', label: 'Ready soonest' },
  { value: 'seed', label: 'Most seed' },
  { value: 'near', label: 'Nearest Kakinada' },
  { value: 'name', label: 'Name A–Z' }
]

/** "Ready now" reads better at a pond than a date the farmer has to subtract from. */
function readyState (nextReadyDate, today) {
  if (!nextReadyDate) return { tone: 'none', label: 'No batch listed', date: null }
  const days = diffDays(today, nextReadyDate)
  if (days <= 0) return { tone: 'now', label: 'Ready now', date: fmtDate(nextReadyDate) }
  if (days === 1) return { tone: 'soon', label: 'Ready tomorrow', date: fmtDate(nextReadyDate) }
  if (days <= 3) return { tone: 'soon', label: `Ready in ${days} days`, date: fmtDate(nextReadyDate) }
  return { tone: '', label: `Ready in ${days} days`, date: fmtDate(nextReadyDate) }
}

export default function Directory () {
  const { data, error, loading, reload } = useAsync(() => api.get('/public/hatcheries'))
  const openRequest = useRequestSeed()

  const [q, setQ] = useState('')
  const [stage, setStage] = useState('')
  const [readyOnly, setReadyOnly] = useState(false)
  const [sort, setSort] = useState('soon')

  const all = data?.hatcheries || []
  const today = todayISO()
  const stages = useMemo(() => [...new Set(all.flatMap(h => h.stages))].sort(), [all])

  const rows = useMemo(() => {
    const filtered = all.filter(h => {
      if (readyOnly && h.liveBatches === 0) return false
      if (stage && !h.stages.includes(stage)) return false
      if (q) {
        const hay = `${h.name} ${h.code} ${h.village} ${h.mandal} ${h.district}`.toLowerCase()
        if (!hay.includes(q.toLowerCase())) return false
      }
      return true
    })

    // A hatchery with nothing listed sorts last however you look at the list.
    const byDate = (a, b) => {
      if (!a.nextReadyDate) return 1
      if (!b.nextReadyDate) return -1
      return a.nextReadyDate.localeCompare(b.nextReadyDate)
    }
    if (sort === 'seed') return [...filtered].sort((a, b) => b.availableLakh - a.availableLakh || byDate(a, b))
    if (sort === 'near') return [...filtered].sort((a, b) => (a.kmFromKakinada ?? 999) - (b.kmFromKakinada ?? 999))
    if (sort === 'name') return [...filtered].sort((a, b) => a.name.localeCompare(b.name))
    return [...filtered].sort(byDate)
  }, [all, q, stage, readyOnly, sort])

  const totals = useMemo(() => ({
    hatcheries: all.length,
    batches: all.reduce((s, h) => s + h.liveBatches, 0),
    seed: all.reduce((s, h) => s + h.availableLakh, 0),
    readyToday: all.filter(h => h.nextReadyDate && diffDays(today, h.nextReadyDate) <= 0).length
  }), [all, today])

  const clear = () => { setQ(''); setStage(''); setReadyOnly(false) }
  const filtering = Boolean(q || stage || readyOnly)

  return (
    <>
      <section className="f-hero">
        <div className="pub-wrap">
          <div className="f-hero-grid">
            <div className="f-hero-main">
              <span className="f-eyebrow">🦐 Kakinada seed desk</span>
              <h1>Seed that is ready,<br />from hatcheries you can check.</h1>
              <p>
                Every hatchery on the belt in one list. Pick one and the desk calls you back.
              </p>

              <div className="f-searchbar">
                <span className="f-searchbar-ic">🔍</span>
                <input
                  type="search"
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  placeholder="Search hatchery or village"
                  aria-label="Search hatchery or village"
                  enterKeyHint="search"
                />
                <button type="button" className="f-btn accent" onClick={() => openRequest({})}>Request seed</button>
              </div>

              {!loading && !error && (
                <div className="f-hero-stats">
                  <div><b>{totals.readyToday}</b><span>ready today</span></div>
                  <div><b>{totals.hatcheries}</b><span>hatcheries</span></div>
                  <div><b>{totals.batches}</b><span>batches</span></div>
                  <div><b>{lakh(totals.seed)}</b><span>seed available</span></div>
                </div>
              )}
            </div>

            {/* On a phone these collapse to a single row of pills — trust stays
                visible without pushing the hatcheries below the fold. */}
            <ul className="f-hero-trust">
              <li><span className="ic">🧪</span><div><b>PCR screened</b><span>WSSV, EHP and IHHNV on every listed batch</span></div></li>
              <li><span className="ic">🎈</span><div><b>Sample tested first</b><span>100 animals at your pond 2 days early — below 90% survival, you walk away</span></div></li>
              <li><span className="ic">🚚</span><div><b>Escorted delivery</b><span>Oxygenated transport with a platform officer alongside</span></div></li>
            </ul>
          </div>
        </div>
      </section>

      <div className="pub-wrap" id="list">
        <div className="f-filters">
          <div className="f-chiprow">
            <button type="button" className={`f-chip ${readyOnly ? 'on' : ''}`} onClick={() => setReadyOnly(v => !v)}>
              ● Seed ready
            </button>
            <button type="button" className={`f-chip ${stage === '' ? 'on' : ''}`} onClick={() => setStage('')}>
              All stages
            </button>
            {stages.map(s => (
              <button type="button" key={s} className={`f-chip ${stage === s ? 'on' : ''}`}
                onClick={() => setStage(stage === s ? '' : s)}>
                {s}
              </button>
            ))}
          </div>

          <div className="f-resultbar">
            <b>{rows.length}</b>
            <span>{rows.length === 1 ? 'hatchery' : 'hatcheries'}</span>
            {filtering && <button type="button" className="f-linkbtn" onClick={clear}>Clear</button>}
            <div className="spacer" />
            <select className="f-sort" value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort hatcheries">
              {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {loading && (
          <div className="f-grid">
            {[0, 1, 2, 3].map(i => <div className="f-card" key={i}><Skeleton rows={4} /></div>)}
          </div>
        )}
        {error && <ErrorBox error={error} onRetry={reload} />}

        {data && (
          <>
            <div className="f-grid">
              {rows.map(h => {
                const ready = readyState(h.nextReadyDate, today)
                const mark = monogram(h.name, h.code || h.name)
                return (
                  <article className={`f-card ${ready.tone === 'now' ? 'is-ready' : ''}`} key={h.id}>
                    <Link className="f-card-body" to={`/hatcheries/${h.id}`}>
                      <div className="f-card-top">
                        <span className="f-mono" style={{ background: mark.bg, color: mark.fg }}>{mark.text}</span>
                        <div className="f-card-id">
                          <h2>{h.name}</h2>
                          <div className="f-place">
                            {h.place}
                            {h.kmFromKakinada != null && <span className="f-dist"> · {h.kmFromKakinada} km from Kakinada</span>}
                          </div>
                        </div>
                      </div>

                      <div className="f-tagrow">
                        <span className={`f-tag ${ready.tone}`}>{ready.label}</span>
                        {ready.date && <span className="f-tag-date">{ready.date}</span>}
                      </div>

                      <div className="f-facts">
                        <div>
                          <b>{h.liveBatches ? lakh(h.availableLakh) : '—'}</b>
                          <span>seed available</span>
                        </div>
                        <div>
                          <b>{h.stages.length ? h.stages.join(', ') : '—'}</b>
                          <span>PL stage</span>
                        </div>
                        <div>
                          <b>{h.liveBatches || '—'}</b>
                          <span>{h.liveBatches === 1 ? 'batch' : 'batches'}</span>
                        </div>
                      </div>

                      <div className="f-marks">
                        {h.pcrCleared && <span className="f-mark good">✓ PCR clear</span>}
                        {h.caaRegistrationNo && <span className="f-mark">✓ CAA registered</span>}
                        {h.broodstockPartners && <span className="f-mark quiet">{h.broodstockPartners}</span>}
                      </div>
                    </Link>

                    <div className="f-card-actions">
                      <Link className="f-btn ghost" to={`/hatcheries/${h.id}`}>See batches</Link>
                      <button type="button" className="f-btn accent" onClick={() => openRequest({ hatchery: h })}>
                        Request this seed
                      </button>
                    </div>
                  </article>
                )
              })}
            </div>

            {!rows.length && (
              <div className="f-empty">
                <b>No hatchery matches that</b>
                <p>Try another village, or just tell the desk your date and we will find seed for it.</p>
                <div className="f-empty-actions">
                  {filtering && <button className="f-btn ghost" onClick={clear}>Clear filters</button>}
                  <button className="f-btn accent" onClick={() => openRequest({})}>Request seed</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
